import { useCallback, useEffect, useState } from "react";
import { apiFetch, ApiError } from "./api";

// M4 — reads the two SEPARATE metrics (+ trend + engine table) computed from the
// site's real `runs`, and triggers an on-demand measurement run. Mirrors the
// backend `computeMetrics` / `runSiteHarness` shapes.

export interface EngineRow {
  persona: string;
  query: string;
  kind: string;
  included: boolean;
  tryIndex: number | null;
  listRank: number | null;
  citedWithLink: boolean;
  isFinalPick: boolean;
  competitors: string[];
}

export interface EngineGroup {
  engine: string;
  label: string;
  rows: EngineRow[];
}

export interface Metrics {
  ok: boolean;
  cadence: string;
  hasRuns: boolean;
  latestWeek: string | null;
  totals: { personas: number; queries: number; runs: number };
  kpis: {
    pctRecommended: number | null;
    pctRecommendedDelta: number | null;
    rankWhenFound: number | null;
    rankWhenFoundDelta: number | null;
    pctFirstTry: number | null;
    shareOfVoice: number | null;
  };
  trend: { week: string; pct: number }[];
  engines: EngineGroup[];
  searchByEngine: Record<string, boolean>;
}

// M11 — POST /run now ENQUEUES (async) and returns the unit count; the consumer
// drains the queue under per-provider rate limits.
export interface EnqueueSummary {
  status: string;
  batchId: string;
  enqueued: number;
  week: string;
  engines: string[];
  personas: number;
  queries: number;
  k: number;
}

export interface RunStatus {
  status: "idle" | "running" | "complete";
  done: number;
  total: number;
  week: string | null;
  personas?: number;
  queries?: number;
}

export interface RunProgress {
  done: number;
  total: number;
}

interface MetricsState {
  data: Metrics | null;
  loading: boolean;
  error: string | null;
  running: boolean;
  progress: RunProgress | null;
  // M13 — true while auto-onboarding / the first run is in flight (no runs yet):
  // the dashboard shows "Analizando tu sitio…" instead of the empty state.
  onboarding: boolean;
  refresh: () => Promise<void>;
  runNow: () => Promise<void>;
}

const POLL_MS = 2500;
const MAX_POLL_MS = 300_000; // stop polling after 5 min (cron/manual still finishes)
const ONBOARD_POLL_MS = 3000;
const ONBOARD_MAX_MS = 180_000; // give up the "Analizando…" auto-poll after 3 min
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export function useMetrics(token: string, siteId: string | null): MetricsState {
  const [data, setData] = useState<Metrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState<RunProgress | null>(null);
  const [onboarding, setOnboarding] = useState(false);

  const refresh = useCallback(async () => {
    if (!siteId) {
      setData(null);
      setLoading(false);
      return;
    }
    try {
      const res = await apiFetch<Metrics>(`/api/sites/${siteId}/metrics`, token);
      setData(res);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.code : "load_failed");
    } finally {
      setLoading(false);
    }
  }, [token, siteId]);

  useEffect(() => {
    setLoading(true);
    void refresh();
  }, [refresh]);

  // M13 — auto-detect onboarding / first-run in flight. When a site has no runs
  // yet and no manual run is active, poll run-status (M11) and refresh metrics on
  // a light interval: show "Analizando…" while onboarding seeds the persona and
  // the first harness run drains, then transition to the normal dashboard the
  // moment the first run's rows land (hasRuns flips → this effect returns).
  useEffect(() => {
    if (!siteId || !data || data.hasRuns || running) {
      setOnboarding(false);
      return;
    }
    let cancelled = false;
    const started = Date.now();
    const initialPersonas = data.totals.personas;

    const poll = async () => {
      let total = 0;
      let personas = initialPersonas;
      try {
        const st = await apiFetch<RunStatus>(`/api/sites/${siteId}/run-status`, token);
        total = st.total ?? 0;
        if (typeof st.personas === "number") personas = st.personas;
      } catch {
        /* transient — keep the current onboarding view */
      }
      // Onboarding is "in flight" until the first persona + batch exist and rows
      // land: no personas yet (still seeding) OR a batch is enqueued (total > 0).
      if (!cancelled) setOnboarding(personas === 0 || total > 0);
      await refresh(); // pulls hasRuns as rows land → flips out of onboarding
    };

    void poll();
    const id = setInterval(() => {
      if (cancelled || Date.now() - started > ONBOARD_MAX_MS) {
        clearInterval(id);
        return;
      }
      void poll();
    }, ONBOARD_POLL_MS);

    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [siteId, data?.hasRuns, running, token, refresh]); // eslint-disable-line react-hooks/exhaustive-deps

  const runNow = useCallback(async () => {
    if (!siteId || running) return;
    setRunning(true);
    setError(null);
    setProgress(null);
    try {
      // Enqueue a single run per (query × engine), full 4-try ladder (K=1) —
      // affordable on demand. The weekly cron does the full K=3.
      const enq = await apiFetch<EnqueueSummary>(`/api/sites/${siteId}/run`, token, {
        method: "POST",
        body: { k: 1 },
      });
      setProgress({ done: 0, total: enq.enqueued });

      // Poll status while the consumer drains; stream partial results into the
      // dashboard as rows land, then a final refresh on completion.
      const started = Date.now();
      while (Date.now() - started < MAX_POLL_MS) {
        await sleep(POLL_MS);
        let st: RunStatus;
        try {
          st = await apiFetch<RunStatus>(`/api/sites/${siteId}/run-status`, token);
        } catch {
          continue; // transient — keep polling
        }
        setProgress({ done: st.done, total: st.total });
        await refresh(); // partial results stream in
        if (st.status === "complete") break;
      }
      await refresh();
    } catch (e) {
      setError(e instanceof ApiError ? e.code : "run_failed");
    } finally {
      setRunning(false);
      setProgress(null);
    }
  }, [token, siteId, running, refresh]);

  return { data, loading, error, running, progress, onboarding, refresh, runNow };
}
